import { ArrowRight } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSEO } from "../hooks/useSEO";
import { Reveal, openSchedule } from "./Reveal";
import FAQSection from "./FAQSection";
import { ABOUT_FAQS } from "../lib/faqData";

const ARVIND_IMG = "https://framerusercontent.com/images/E0FjDZIgpyrIMVu7H1x9oWTO1U.jpg";
const TOM_IMG = "https://framerusercontent.com/images/92I2APMroZ6vjD93pcOlDcBAyGM.jpg";

const CULTURE_VALUES = [
  { title: "Respect & Integrity", text: "Respect and integrity are the foundations of our workplace culture. We celebrate the diversity of our team, recognizing that our different backgrounds and perspectives make us stronger." },
  { title: "Stronger Together", text: "We embrace the power of unity, knowing that working together makes us stronger. When we lift each other up, we create a positive space for growth and success." },
  { title: "Move Fast, Improve Always", text: "We believe in moving quickly while continuously refining our process. Every action is a chance to learn, and we take feedback seriously — from successes or setbacks." },
  { title: "Ownership", text: "We all take responsibility and dive in to get things done, collaborating as one team and tackling challenges for the business and our customers." },
  { title: "Experiment & Evolve", text: "We love experimenting and trying out different ways to improve — always looking for smarter ways to work, serve and grow." },
];

const Section = ({ eyebrow, title, children, testId }) => (
  <div className="max-w-[860px] mx-auto px-6 mt-20 lg:mt-28" data-testid={testId}>
    <Reveal>
      <div className="font-mono text-[11px] tracking-[0.35em] text-skope uppercase">{eyebrow}</div>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="mt-4 font-display font-bold text-2xl lg:text-4xl tracking-tight text-ink">{title}</h2>
    </Reveal>
    <div className="mt-6">{children}</div>
  </div>
);

const FounderCard = ({ img, name, role, quote, testId, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="border border-ink/10 bg-white" data-testid={testId}>
      <div className="aspect-[4/3] overflow-hidden bg-[#f2f2f0]">
        <img src={img} alt={`${name} — ${role}, Skope Kitchens`} loading="lazy"
          className="w-full h-full object-cover object-top"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div className="p-6 lg:p-8">
        <h3 className="font-display font-bold text-xl lg:text-2xl text-ink tracking-tight">{name}</h3>
        <div className="mt-1 font-mono text-[11px] tracking-[0.25em] text-skope uppercase">{role}</div>
        <p className="mt-4 text-ink/60 leading-relaxed text-sm lg:text-base">{quote}</p>
      </div>
    </div>
  </Reveal>
);

const AboutPage = () => {
  useSEO({
    title: "About Skope Kitchens – Restaurant Solutions in Bangalore",
    description:
      "Discover Skope Kitchens: restaurant business support, kitchen innovation, and culinary leadership for Bangalore's food brands.",
    canonical: "https://www.skopekitchens.com/about-us",
  });

  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="about-page">
      <Navbar />
      <main className="pb-24">
        <div className="bg-ink bg-blueprint text-white pt-32 lg:pt-40 pb-12 lg:pb-16">
          <div className="max-w-[860px] mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-skope" />
                Our Story
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display font-bold text-3xl lg:text-5xl tracking-tight leading-[1.1] text-white" data-testid="page-h1">
                Powering the Future of Restaurant Business.
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-5 text-white/55 leading-relaxed max-w-2xl">
                At Skope Kitchens, we help grow your restaurant business with modern, delivery-optimized kitchens. Our
                cloud-first model empowers food brands to scale faster, cut costs, and deliver excellence. From small
                teams to multi-brand operators, we transform every restaurant kitchen setup into a launchpad for growth
                and community.
              </p>
            </Reveal>
          </div>
        </div>

        <Section eyebrow="Message from Our Founders" title="The people behind the kitchens." testId="founders-section">
          <Reveal>
            <p className="text-ink/60 leading-relaxed">
              Our journey started with a simple idea: to give restaurant brands the freedom to grow without limits.
              Today, we've built an inclusive, delivery-first platform that simplifies the restaurant business. Join us
              as we make online food delivery for restaurants scalable, seamless, and sustainable.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <FounderCard
              img={ARVIND_IMG}
              name="Arvind Krishnan"
              role="Founder & CEO"
              quote="We believe it's essential to ensure food is always safe and healthy, whether you're dining out or ordering in. At Skope Kitchens, we focus on online food delivery for restaurants by partnering with top F&B brands — helping them thrive in the cloud kitchen ecosystem, ensuring customers receive high-quality meals every time."
              testId="founder-arvind"
            />
            <FounderCard
              img={TOM_IMG}
              name="Tom Mathew"
              role="Co-Founder & COO"
              quote="At Skope Kitchens, we understand that delivering both quality and convenience is challenging, especially in the restaurant business. Today's customers expect speed without compromising taste. That's why we leverage smart technology and operational excellence to help food brands provide fast service without sacrificing quality."
              testId="founder-tom"
              delay={0.08}
            />
          </div>
        </Section>

        <Section eyebrow="Our Mission" title="Transforming Culinary Spaces with Purpose and Passion" testId="mission-section">
          <div className="space-y-4 text-ink/60 leading-relaxed">
            <p>At Skope Kitchens, we transform culinary spaces into delivery-optimised environments where restaurant technology meets operational efficiency.</p>
            <p>Our mission is to empower food brands with scalable, sustainable restaurant kitchen setups that enhance productivity and profitability.</p>
            <p>With a strong focus on innovation and community, we help entrepreneurs and established operators grow their restaurant business, one smart kitchen at a time.</p>
          </div>
        </Section>

        <Section eyebrow="Our Vision" title="Shaping a Thriving Future for High-Potential Brands" testId="vision-section">
          <div className="space-y-4 text-ink/60 leading-relaxed">
            <p>To be the leading innovator in providing one-stop solutions that empower high-potential brands to scale efficiently. We aim to minimize risk while maximizing profitability in the restaurant business.</p>
            <p>Our vision is to create a future where our holistic approach, rooted in restaurant technology and operational expertise, helps brands thrive in a competitive culinary landscape, turning challenges into growth opportunities.</p>
          </div>
        </Section>

        <Section eyebrow="Milestones & Impact" title="Driving Restaurant Growth Through Speed, Scale, and Precision" testId="milestones-section">
          <div className="space-y-4 text-ink/60 leading-relaxed">
            <p>At Skope Kitchens, we've built a track record of empowering restaurant brands to expand faster and smarter.</p>
            <p>Our cloud-enabled kitchens reduce setup time, lower capital costs, and help our partners go live in as little as 10 days. From launching delivery-only restaurants in India to supporting multi-brand expansion across Bangalore, our model proves that a well-designed restaurant business model combined with the right infrastructure delivers real results.</p>
            <p>Every restaurant kitchen setup is optimized for efficiency, scalability, and growth, whether you're a food startup or a national chain.</p>
          </div>
        </Section>

        <Section eyebrow="Our Approach & Technology" title="Kitchen-as-a-Service Built for Restaurant Success" testId="approach-section">
          <div className="space-y-4 text-ink/60 leading-relaxed">
            <p>Skope Kitchens offers a delivery-optimized, plug-and-play model tailored for today's restaurant operators. Our cloud kitchen setup cost is significantly lower than traditional formats, giving brands the freedom to scale with minimal risk.</p>
            <p>From real-time dashboards to restaurant management software integration, we bring technology, speed, and service under one roof.</p>
            <p>Whether you're launching your first outlet or expanding across locations, our restaurant kitchen management software ensures seamless operations and better margins, so you can focus on what you do best: serving great food.</p>
          </div>
        </Section>

        <div className="max-w-[860px] mx-auto px-6 mt-20 lg:mt-28" data-testid="culture-section">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.35em] text-skope uppercase">How We Work Together</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display font-bold text-2xl lg:text-4xl tracking-tight text-ink">
              The values inside our kitchens.
            </h2>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {CULTURE_VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.05 * i}>
                <div className="bg-white p-6 h-full" data-testid={`culture-value-${i}`}>
                  <h3 className="font-display font-semibold text-lg text-ink">{v.title}</h3>
                  <p className="mt-2 text-sm text-ink/55 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </main>

      <FAQSection faqs={ABOUT_FAQS} idPrefix="about-faq" source="about-us" />

      <div className="max-w-[860px] mx-auto px-6 py-20">
        <div className="border border-skope/30 bg-ink text-white p-8 lg:p-10" data-testid="about-cta">
          <div className="font-mono text-[10px] tracking-[0.3em] text-skope uppercase">Schedule a Call</div>
          <h2 className="mt-3 font-display font-semibold text-2xl text-white tracking-tight">Building a food brand?</h2>
          <p className="mt-3 text-white/55 leading-relaxed max-w-lg">
            Let's build the kitchen and operating system behind it.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <button onClick={() => openSchedule()} className="flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-white hover:text-ink transition-colors" data-testid="about-cta-schedule">
              Schedule a Call <ArrowRight size={14} />
            </button>
            <a href="/#calculator" className="flex items-center justify-center gap-3 border border-white/25 text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors" data-testid="about-cta-calculator">
              Calculate Your Kitchen
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AboutPage;
